import SEO from './../components/SEO';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  FileText,
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  FileSpreadsheet,
  Lock,
  Pencil,
  Mail,
  Link2,
  MessageCircle,
  Upload,
  Archive,
  Square,
  CheckSquare,
  TrendingUp,
  CircleDollarSign,
  AlertTriangle,
  BookmarkPlus,
  Trash2,
} from 'lucide-react';
import { listInvoices, getInvoiceStats, downloadInvoicePdf, downloadPDF, exportInvoicesCsv, downloadCSV, getInvoice, updateInvoiceStatus, shareInvoice, bulkDownloadPdfs, downloadImportTemplate, importInvoicesCsv, listDrafts, deleteDraft } from '../services/api';
import { formatCurrency } from '../utils/currencyFormatter';
import { useEntitlements, Features } from '../hooks/useEntitlements';
import EmailInvoiceModal from '../components/EmailInvoiceModal';
import UpgradeModal from '../components/UpgradeModal';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Default page size */
const DEFAULT_LIMIT = 20;

/** Available page size options */
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/** Payment status filter options */
const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'overdue', label: 'Overdue' },
];

/** Invoice status filter options */
const INVOICE_STATUS_OPTIONS = [
  { value: '', label: 'All Records' },
  { value: 'finalized', label: 'Finalized' },
  { value: 'imported', label: 'Imported' },
];

/** Invoice type options for filter dropdown */
const INVOICE_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'freelancer', label: 'Freelancer' },
  { value: 'coach', label: 'Coach/Consultant' },
  { value: 'gst', label: 'GST (India)' },
  { value: 'vat', label: 'VAT Invoice' },
  { value: 'international', label: 'International' },
];

/** Invoice type display names */
const invoiceTypeNames = {
  freelancer: 'Freelancer',
  coach: 'Coach/Consultant',
  gst: 'GST (India)',
  vat: 'VAT Invoice',
  international: 'International',
};

/** Debounce delay for search input (ms) */
const SEARCH_DEBOUNCE_MS = 300;

/** Return a human-readable "X ago" string from an ISO timestamp */
function timeAgo(isoString, now = Date.now()) {
  const diff = now - new Date(isoString).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 2) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

const STATUS_STYLES = {
  paid: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400',
  unpaid: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400',
  overdue: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400',
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format date for display
 */
function formatDate(isoString) {
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Parse integer from string with fallback
 */
function parseIntSafe(value, fallback) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) || parsed < 0 ? fallback : parsed;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Invoice History Page
 * 
 * Displays a paginated list of finalized invoices with download functionality.
 * Frontend is a pure renderer - all data comes from backend.
 * 
 * Features:
 * - Pagination via URL query params (limit/offset)
 * - Filter by invoice type
 * - Search by invoice number
 * - Page size selector
 * - Accurate "Page X of Y" using total_count from backend
 * - Preserves state across refresh and browser navigation
 * - Links to detail page with return context
 */
export default function InvoiceHistoryPage() {
  const navigate = useNavigate();

  // Feature entitlements
  const { isFeatureEnabled } = useEntitlements();
  const canViewHistory   = isFeatureEnabled(Features.INVOICE_HISTORY);
  const canExportCsv     = isFeatureEnabled(Features.INVOICE_EXPORT_CSV);
  const canDownloadPdf   = isFeatureEnabled(Features.INVOICE_PDF_DOWNLOAD);
  const canEmail         = isFeatureEnabled(Features.INVOICE_EMAIL);
  const canShare         = isFeatureEnabled(Features.INVOICE_SHARE);
  const canBulkDownload  = isFeatureEnabled(Features.INVOICE_BULK_DOWNLOAD);
  const canSaveDraft     = isFeatureEnabled(Features.INVOICE_DRAFT_SAVE);

  // Upgrade modal state (shown when free user hits a gated feature)
  const [upgradeModal, setUpgradeModal] = useState({ open: false, reason: '' });
  const showUpgrade = useCallback((reason) => setUpgradeModal({ open: true, reason }), []);
  
  // URL state for pagination and filters
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  // Parse pagination and filter params from URL with defaults
  const limit = useMemo(() => {
    const urlLimit = parseIntSafe(searchParams.get('limit'), DEFAULT_LIMIT);
    return PAGE_SIZE_OPTIONS.includes(urlLimit) ? urlLimit : DEFAULT_LIMIT;
  }, [searchParams]);
  
  const offset = useMemo(() => 
    parseIntSafe(searchParams.get('offset'), 0),
    [searchParams]
  );
  
  const invoiceType = useMemo(() => 
    searchParams.get('invoice_type') || '',
    [searchParams]
  );
  
  const searchQuery = useMemo(() =>
    searchParams.get('q') || '',
    [searchParams]
  );

  const statusFilter = useMemo(() =>
    searchParams.get('payment_status') || '',
    [searchParams]
  );

  const invoiceStatusFilter = useMemo(() =>
    searchParams.get('invoice_status') || '',
    [searchParams]
  );

  // Stats bar
  const [stats, setStats] = useState(null);

  // Component state
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [sharingId, setSharingId] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [importModal, setImportModal] = useState(false);

  // Drafts state
  const [drafts, setDrafts] = useState([]);
  const [deletingDraftId, setDeletingDraftId] = useState(null);

  // Tick every 60s so "X ago" labels stay accurate without a full refetch
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  // Fetch drafts when the user has Pro
  useEffect(() => {
    if (!canSaveDraft) return;
    listDrafts().then(res => setDrafts(res.drafts || [])).catch(() => {});
  }, [canSaveDraft]);

  const handleContinueDraft = useCallback((draft) => {
    navigate(`/create/${draft.invoice_type}`, {
      state: { draftId: draft.id },
    });
  }, [navigate]);

  const handleDeleteDraft = useCallback(async (draft) => {
    setDeletingDraftId(draft.id);
    try {
      await deleteDraft(draft.id);
      setDrafts(prev => prev.filter(d => d.id !== draft.id));
    } catch (err) {
      setError(`Failed to delete draft: ${err.message}`);
    } finally {
      setDeletingDraftId(null);
    }
  }, []);

  // Email modal state
  const [emailModal, setEmailModal] = useState({ open: false, formData: null, invoiceType: null, loadingId: null, lastEmailedAt: null, emailCount: 0 });
  
  // Local search input (for debouncing)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const searchTimeoutRef = useRef(null);

  // Calculated pagination values
  const totalPages = Math.ceil(totalCount / limit) || 1;
  const currentPage = Math.floor(offset / limit) + 1;
  const hasPrev = offset > 0;
  const hasNext = offset + invoices.length < totalCount;

  /**
   * Update URL search params for pagination and filters
   * Only includes params that have non-default values
   */
  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams();

    const newLimit = updates.limit ?? limit;
    const newOffset = updates.offset ?? offset;
    const newType = updates.invoiceType ?? invoiceType;
    const newQ = updates.q ?? searchQuery;
    const newStatus = updates.statusFilter ?? statusFilter;
    const newInvoiceStatus = updates.invoiceStatusFilter ?? invoiceStatusFilter;

    // Always include limit/offset if non-default
    if (newLimit !== DEFAULT_LIMIT) {
      newParams.set('limit', String(newLimit));
    }
    if (newOffset > 0) {
      newParams.set('offset', String(newOffset));
    }
    // Include filters only if set
    if (newType) {
      newParams.set('invoice_type', newType);
    }
    if (newQ) {
      newParams.set('q', newQ);
    }
    if (newStatus) {
      newParams.set('payment_status', newStatus);
    }
    if (newInvoiceStatus) {
      newParams.set('invoice_status', newInvoiceStatus);
    }

    setSearchParams(newParams, { replace: true });
  }, [limit, offset, invoiceType, searchQuery, statusFilter, invoiceStatusFilter, setSearchParams]);

  // Fetch stats — called on mount and whenever invoices/statuses change
  const refreshStats = useCallback(() => {
    getInvoiceStats().then(setStats).catch(() => {});
  }, []);

  useEffect(() => { refreshStats(); }, [refreshStats]);

  /**
   * Fetch invoices — used by the manual Refresh button only.
   * Auto-fetching on pagination/filter changes is handled by the effect below.
   */
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listInvoices({
        limit,
        offset,
        invoiceType: invoiceType || undefined,
        q: searchQuery || undefined,
        paymentStatus: statusFilter || undefined,
        invoiceStatus: invoiceStatusFilter || undefined,
      });
      setInvoices(response.invoices || []);
      setTotalCount(response.total_count || 0);
      refreshStats();
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [limit, offset, invoiceType, searchQuery, statusFilter, invoiceStatusFilter, refreshStats]);

  // Auto-fetch when pagination or filters change.
  // The cleanup function sets `active = false` so stale responses from
  // React StrictMode's double-mount (dev only) are ignored.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    listInvoices({
      limit,
      offset,
      invoiceType: invoiceType || undefined,
      q: searchQuery || undefined,
      paymentStatus: statusFilter || undefined,
      invoiceStatus: invoiceStatusFilter || undefined,
    })
      .then((response) => {
        if (!active) return;
        setInvoices(response.invoices || []);
        setTotalCount(response.total_count || 0);
      })
      .catch((err) => {
        if (!active) return;
        console.error('Error fetching invoices:', err);
        setError(err.message || 'Failed to load invoices');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [limit, offset, invoiceType, searchQuery, statusFilter, invoiceStatusFilter]);

  // Sync local search input with URL param
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  /**
   * Handle search input change with debounce
   */
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      updateSearchParams({ q: value, offset: 0 }); // Reset to first page on search
    }, SEARCH_DEBOUNCE_MS);
  }, [updateSearchParams]);

  /**
   * Clear search input
   */
  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    updateSearchParams({ q: '', offset: 0 });
  }, [updateSearchParams]);

  /**
   * Handle invoice type filter change
   */
  const handleTypeChange = useCallback((e) => {
    updateSearchParams({ invoiceType: e.target.value, offset: 0 });
  }, [updateSearchParams]);

  /**
   * Handle payment status filter change
   */
  const handleStatusFilterChange = useCallback((e) => {
    updateSearchParams({ statusFilter: e.target.value, offset: 0 });
  }, [updateSearchParams]);

  const handleInvoiceStatusFilterChange = useCallback((e) => {
    updateSearchParams({ invoiceStatusFilter: e.target.value, offset: 0 });
  }, [updateSearchParams]);

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((e) => {
    const newLimit = parseInt(e.target.value, 10);
    updateSearchParams({ limit: newLimit, offset: 0 }); // Reset to first page
  }, [updateSearchParams]);

  /**
   * Go to previous page
   */
  const handlePrevPage = useCallback(() => {
    const newOffset = Math.max(0, offset - limit);
    updateSearchParams({ offset: newOffset });
  }, [limit, offset, updateSearchParams]);

  /**
   * Go to next page
   */
  const handleNextPage = useCallback(() => {
    const newOffset = offset + limit;
    updateSearchParams({ offset: newOffset });
  }, [limit, offset, updateSearchParams]);

  /**
   * Handle PDF download for an invoice
   */
  const handleDownload = async (invoiceId, invoiceNumber) => {
    setDownloadingId(invoiceId);
    setDownloadSuccess(null);
    
    try {
      const { blob, filename } = await downloadInvoicePdf(invoiceId);
      downloadPDF(blob, filename);
      setDownloadSuccess(invoiceId);
      
      // Clear success message after 3 seconds
      setTimeout(() => setDownloadSuccess(null), 3000);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(`Failed to download ${invoiceNumber}: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  /**
   * Handle CSV export with current filters
   */
  const handleExport = async () => {
    setExporting(true);
    
    try {
      const { blob, filename } = await exportInvoicesCsv({
        invoiceType: invoiceType || undefined,
        q: searchQuery || undefined,
      });
      downloadCSV(blob, filename);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError(`Failed to export CSV: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleOpenEmail = useCallback(async (invoice) => {
    if (!canEmail) { showUpgrade('Emailing invoices is a Pro feature.'); return; }
    setEmailModal({ open: false, formData: null, invoiceType: null, loadingId: invoice.id, lastEmailedAt: null, emailCount: 0 });
    try {
      const result = await getInvoice(invoice.id);
      const formData = {
        ...result.data,
        invoice_number: result.invoice_number,
        invoice_type: result.invoice_type,
      };
      setEmailModal({
        open: true,
        formData,
        invoiceType: result.invoice_type,
        loadingId: null,
        lastEmailedAt: invoice.last_emailed_at || null,
        emailCount: invoice.email_count || 0,
      });
    } catch (err) {
      setEmailModal({ open: false, formData: null, invoiceType: null, loadingId: null, lastEmailedAt: null, emailCount: 0 });
      setError(`Failed to load invoice: ${err.message}`);
    }
  }, [canEmail, showUpgrade]);

  // invoice_status === 'imported'   → imported, not yet completed (actions locked, Complete CTA)
  // invoice_status === 'completed'  → was imported, now has PDF (actions live, badge still shows)
  // invoice_status === 'finalized'  → natively created (no badge)
  const isImported  = (invoice) => invoice.invoice_status === 'imported';
  const wasImported = (invoice) => invoice.invoice_status === 'imported' || invoice.invoice_status === 'completed';

  /**
   * Navigate to invoice editor in edit mode with the invoice data prefilled
   */
  const handleEdit = useCallback((invoice) => {
    navigate(`/create/${invoice.invoice_type}`, {
      state: {
        editInvoiceId: invoice.id,
        editInvoiceNumber: invoice.invoice_number,
      }
    });
  }, [navigate]);

  const handleToggleStatus = useCallback(async (invoice) => {
    const currentStored = invoice.payment_status === 'paid' ? 'paid' : 'unpaid';
    const next = currentStored === 'paid' ? 'unpaid' : 'paid';
    setStatusUpdating(invoice.id);
    try {
      await updateInvoiceStatus(invoice.id, next);
      setInvoices(prev => prev.map(inv =>
        inv.id === invoice.id ? { ...inv, payment_status: next } : inv
      ));
      refreshStats();
    } catch (err) {
      setError(`Failed to update status: ${err.message}`);
    } finally {
      setStatusUpdating(null);
    }
  }, [refreshStats]);

  const handleShare = useCallback(async (invoice) => {
    if (!canShare) { showUpgrade('Share links are a Pro feature.'); return; }
    setSharingId(invoice.id);
    setShareSuccess(null);
    try {
      const { share_token } = await shareInvoice(invoice.id);
      const url = `${window.location.origin}/view/${share_token}`;
      await navigator.clipboard.writeText(url);
      setShareSuccess(invoice.id);
      setTimeout(() => setShareSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to create share link: ${err.message}`);
    } finally {
      setSharingId(null);
    }
  }, [canShare, showUpgrade]);

  const handleWhatsAppShare = useCallback(async (invoice) => {
    if (!canShare) { showUpgrade('Share links are a Pro feature.'); return; }
    setSharingId(invoice.id);
    try {
      const { share_token } = await shareInvoice(invoice.id);
      const url = `${window.location.origin}/view/${share_token}`;
      const text = `Hi, please find your invoice ${invoice.invoice_number} here: ${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(`Failed to create share link: ${err.message}`);
    } finally {
      setSharingId(null);
    }
  }, [canShare, showUpgrade]);

  // Bulk selection helpers
  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const finalizableInvoices = useMemo(() => invoices.filter(inv => !isImported(inv)), [invoices]);
  const allPageSelected = finalizableInvoices.length > 0 && finalizableInvoices.every(inv => selectedIds.has(inv.id));
  const handleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (finalizableInvoices.every(inv => prev.has(inv.id))) {
        const next = new Set(prev);
        finalizableInvoices.forEach(inv => next.delete(inv.id));
        return next;
      }
      const next = new Set(prev);
      finalizableInvoices.forEach(inv => next.add(inv.id));
      return next;
    });
  }, [finalizableInvoices]);

  const handleBulkDownload = useCallback(async () => {
    if (!canBulkDownload) { showUpgrade('Bulk PDF download is a Pro feature.'); return; }
    if (!selectedIds.size) return;
    setBulkDownloading(true);
    try {
      const { blob, filename } = await bulkDownloadPdfs(Array.from(selectedIds));
      downloadPDF(blob, filename);
      setSelectedIds(new Set());
    } catch (err) {
      setError(`Bulk download failed: ${err.message}`);
    } finally {
      setBulkDownloading(false);
    }
  }, [canBulkDownload, showUpgrade, selectedIds]);

  // Build "from" state for detail page links (preserves pagination context)
  const fromState = useMemo(() => ({
    from: location.pathname + location.search
  }), [location.pathname, location.search]);

  // Pagination info text
  const paginationInfo = useMemo(() => {
    if (invoices.length === 0) return '';
    const start = offset + 1;
    const end = offset + invoices.length;
    return `Showing ${start}–${end} of ${totalCount}`;
  }, [offset, invoices.length, totalCount]);

  // Check if any filters are active
  const hasActiveFilters = invoiceType || searchQuery || statusFilter || invoiceStatusFilter;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO title="Invoice History | Invilic" description="View and manage your past invoices." canonical="https://www.invilic.com/history" noindex={true} />
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline ml-2">Back to Home</span>
              </Link>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-700" />
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600" />
                Invoice History
              </h1>
            </div>
            <button
              onClick={fetchInvoices}
              disabled={loading}
              className="flex items-center px-2.5 sm:px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </header>

      {/* Upgrade wall — free users see this instead of the invoice list */}
      {!canViewHistory && (
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Invoice History is a Pro Feature</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto">
            Upgrade to Pro to access your full invoice history, search, export, and payment tracking.
          </p>
          <button
            onClick={() => showUpgrade('Invoice History requires a Pro plan.')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors shadow-md shadow-blue-600/20"
          >
            Upgrade to Pro: ₹299/month
          </button>
          {upgradeModal.open && (
            <UpgradeModal reason={upgradeModal.reason} onClose={() => setUpgradeModal({ open: false, reason: '' })} />
          )}
        </main>
      )}

      {/* Main Content */}
      {canViewHistory && <main className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Stats Bar */}
        {stats && (
          <div className="mb-6 grid grid-cols-3 gap-2 sm:gap-4">
            {/* This Month */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl px-2 py-3 sm:px-4 sm:py-4 flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-center sm:gap-3">
              <div className="hidden sm:flex w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-wide">Month</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-none mt-0.5">
                  {stats.this_month_count}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {stats.this_month_count === 1 ? 'invoice' : 'invoices'}
                </p>
              </div>
            </div>

            {/* Unpaid */}
            <div className={`border rounded-xl px-2 py-3 sm:px-4 sm:py-4 flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-center sm:gap-3 ${
              stats.unpaid_count > 0
                ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800'
            }`}>
              <div className={`hidden sm:flex w-9 h-9 rounded-lg items-center justify-center flex-shrink-0 ${
                stats.unpaid_count > 0 ? 'bg-amber-100 dark:bg-amber-900/40' : 'bg-gray-100 dark:bg-gray-800'
              }`}>
                <CircleDollarSign className={`w-4 h-4 ${
                  stats.unpaid_count > 0 ? 'text-amber-500' : 'text-gray-400'
                }`} />
              </div>
              <div className="min-w-0">
                <p className={`text-[10px] sm:text-xs font-semibold uppercase tracking-wide ${
                  stats.unpaid_count > 0 ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'
                }`}>Unpaid</p>
                <p className={`text-xl sm:text-2xl font-bold leading-none mt-0.5 ${
                  stats.unpaid_count > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-gray-900 dark:text-white'
                }`}>
                  {stats.unpaid_count}
                </p>
                <p className={`text-[10px] sm:text-xs mt-0.5 ${
                  stats.unpaid_count > 0 ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {stats.unpaid_count === 1 ? 'invoice' : 'invoices'}
                </p>
              </div>
            </div>

            {/* Overdue */}
            <div className={`border rounded-xl px-2 py-3 sm:px-4 sm:py-4 flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-center sm:gap-3 ${
              stats.overdue_count > 0
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800'
            }`}>
              <div className={`hidden sm:flex w-9 h-9 rounded-lg items-center justify-center flex-shrink-0 ${
                stats.overdue_count > 0 ? 'bg-red-100 dark:bg-red-900/40' : 'bg-gray-100 dark:bg-gray-800'
              }`}>
                <AlertTriangle className={`w-4 h-4 ${
                  stats.overdue_count > 0 ? 'text-red-500' : 'text-gray-400'
                }`} />
              </div>
              <div className="min-w-0">
                <p className={`text-[10px] sm:text-xs font-semibold uppercase tracking-wide ${
                  stats.overdue_count > 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
                }`}>Overdue</p>
                <p className={`text-xl sm:text-2xl font-bold leading-none mt-0.5 ${
                  stats.overdue_count > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                }`}>
                  {stats.overdue_count}
                </p>
                <p className={`text-[10px] sm:text-xs mt-0.5 ${
                  stats.overdue_count > 0 ? 'text-red-400 dark:text-red-500' : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {stats.overdue_count === 1 ? 'invoice' : 'invoices'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Drafts section (Pro only) */}
        {canSaveDraft && drafts.length > 0 && (
          <div className="mb-6">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              <BookmarkPlus className="w-4 h-4" />
              Saved Drafts
            </h2>
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-violet-200 dark:border-violet-800 divide-y divide-violet-100 dark:divide-violet-900 overflow-hidden">
              {drafts.map(draft => (
                <div key={draft.id} className="flex items-center gap-4 px-4 py-3 hover:bg-violet-50 dark:hover:bg-violet-900/10 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center flex-shrink-0">
                    <BookmarkPlus className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                        {draft.business_name || draft.client_name || 'Untitled Draft'}
                      </span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">
                        Draft
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                        {invoiceTypeNames[draft.invoice_type] || draft.invoice_type}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {draft.client_name && draft.business_name ? `For ${draft.client_name} · ` : ''}
                      Saved {timeAgo(draft.created_at, now)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleContinueDraft(draft)}
                      className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/40 hover:bg-violet-200 dark:hover:bg-violet-900/60 rounded-lg transition-colors"
                      title="Continue editing"
                    >
                      <Pencil className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="hidden sm:inline">Continue</span>
                    </button>
                    <button
                      onClick={() => handleDeleteDraft(draft)}
                      disabled={deletingDraftId === draft.id}
                      className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete draft"
                    >
                      {deletingDraftId === draft.id
                        ? <RefreshCw className="w-4 h-4 animate-spin" />
                        : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <div className="mb-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex flex-col gap-3">
            {/* Search — always full width */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoice number…"
                value={searchInput}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filter dropdowns + action buttons
                Mobile: 2-column grid so dropdowns sit side-by-side and actions share a row
                Desktop (sm+): single flex row */}
            <div className="grid grid-cols-2 sm:flex sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
              {/* Type Filter */}
              <div className="sm:w-44">
                <select
                  value={invoiceType}
                  onChange={handleTypeChange}
                  className="w-full py-2 px-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  aria-label="Filter by invoice type"
                >
                  {INVOICE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Status Filter */}
              <div className="sm:w-40">
                <select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="w-full py-2 px-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  aria-label="Filter by payment status"
                >
                  {PAYMENT_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Invoice Status Filter — spans both cols on mobile */}
              <div className="col-span-2 sm:col-auto sm:w-36">
                <select
                  value={invoiceStatusFilter}
                  onChange={handleInvoiceStatusFilterChange}
                  className="w-full py-2 px-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  aria-label="Filter by invoice status"
                >
                  {INVOICE_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Export + Import — share a row on mobile, individual items on desktop */}
              <div className="col-span-2 sm:contents flex gap-2">
                {/* Export CSV Button - Feature gated */}
                {canExportCsv ? (
                  <button
                    onClick={handleExport}
                    disabled={exporting || loading}
                    className="flex-1 sm:flex-none flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Export to CSV"
                  >
                    {exporting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span className="ml-2 hidden sm:inline">{exporting ? 'Exporting...' : 'Export CSV'}</span>
                    <span className="ml-1.5 sm:hidden text-xs">{exporting ? 'Exporting…' : 'Export'}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => showUpgrade('CSV export is a Pro feature.')}
                    className="flex-1 sm:flex-none flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="ml-2 hidden sm:inline">Export CSV</span>
                    <span className="ml-1.5 sm:hidden text-xs">Export</span>
                  </button>
                )}
                {/* Import CSV */}
                <button
                  onClick={() => setImportModal(true)}
                  className="flex-1 sm:flex-none flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Upload className="w-4 h-4 flex-shrink-0" />
                  <span className="ml-2 hidden sm:inline">Import CSV</span>
                  <span className="ml-1.5 sm:hidden text-xs">Import</span>
                </button>
              </div>
            </div>
          </div>

          {/* Bulk selection toolbar */}
          {canDownloadPdf && selectedIds.size > 0 && (
            <div className="mt-3 flex items-center gap-3 px-4 py-2.5 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Archive className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                {selectedIds.size} invoice{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={handleBulkDownload}
                disabled={bulkDownloading}
                className="flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {bulkDownloading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Download ZIP
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="ml-auto text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear
              </button>
            </div>
          )}

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>Active filters:</span>
              {invoiceType && (
                <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                  Type: {invoiceTypeNames[invoiceType] || invoiceType}
                </span>
              )}
              {statusFilter && (
                <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                  Status: {PAYMENT_STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || statusFilter}
                </span>
              )}
              {invoiceStatusFilter && (
                <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                  Record: {INVOICE_STATUS_OPTIONS.find(o => o.value === invoiceStatusFilter)?.label || invoiceStatusFilter}
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                  Search: "{searchQuery}"
                </span>
              )}
              <button
                onClick={() => {
                  setSearchInput('');
                  updateSearchParams({ invoiceType: '', q: '', statusFilter: '', invoiceStatusFilter: '', offset: 0 });
                }}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 underline ml-2"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mb-4" />
            <p className="text-gray-600">Loading invoices...</p>
          </div>
        )}

        {/* Empty State - No invoices at all */}
        {!loading && invoices.length === 0 && !hasActiveFilters && offset === 0 && (
          <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <FileText className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Invoices Yet</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-center max-w-md">
              You haven't created any invoices yet. Create your first invoice to see it appear here.
            </p>
            <Link
              to="/"
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Your First Invoice
            </Link>
          </div>
        )}

        {/* Empty State - No results with filters */}
        {!loading && invoices.length === 0 && hasActiveFilters && (
          <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <Search className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Matching Invoices</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-center max-w-md">
              No invoices match your current filters. Try adjusting your search or filter criteria.
            </p>
            <button
              onClick={() => {
                setSearchInput('');
                updateSearchParams({ invoiceType: '', q: '', statusFilter: '', offset: 0 });
              }}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* Empty page (offset > 0 but no results) */}
        {!loading && invoices.length === 0 && offset > 0 && !hasActiveFilters && (
          <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <FileText className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No More Invoices</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-center max-w-md">
              You've reached the end of your invoice history.
            </p>
            <button
              onClick={() => updateSearchParams({ offset: 0 })}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to First Page
            </button>
          </div>
        )}

        {/* Invoice List */}
        {!loading && invoices.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">

            {/* ── Mobile card list (< sm) ── */}
            <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                  {/* Top row: number + amount/status */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Link
                          to={`/history/${invoice.id}`}
                          state={fromState}
                          className="flex items-center gap-1.5 font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{invoice.invoice_number}</span>
                        </Link>
                        {wasImported(invoice) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                            Imported
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {invoiceTypeNames[invoice.invoice_type] || invoice.invoice_type}
                        {' · '}
                        {formatDate(invoice.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <span className="font-bold text-gray-900 dark:text-white text-sm">
                        {formatCurrency(invoice.total, invoice.currency)}
                      </span>
                      <button
                        onClick={() => handleToggleStatus(invoice)}
                        disabled={statusUpdating === invoice.id}
                        title={invoice.payment_status === 'paid' ? 'Mark as unpaid' : 'Mark as paid'}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold transition-opacity disabled:opacity-50 cursor-pointer ${STATUS_STYLES[invoice.payment_status] || STATUS_STYLES.unpaid}`}
                      >
                        {statusUpdating === invoice.id
                          ? <RefreshCw className="w-3 h-3 animate-spin" />
                          : invoice.payment_status === 'paid' ? 'Paid'
                          : invoice.payment_status === 'overdue' ? 'Overdue' : 'Unpaid'}
                      </button>
                    </div>
                  </div>

                  {/* Bottom row: actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-0.5">
                      {/* Edit */}
                      <button
                        onClick={() => handleEdit(invoice)}
                        className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title={isImported(invoice) ? 'Complete invoice' : 'Edit invoice'}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {/* Share — locked on imported */}
                      {isImported(invoice) ? (
                        <span className="p-2 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Complete the invoice to share it">
                          <Link2 className="w-4 h-4" />
                        </span>
                      ) : (
                        <button
                          onClick={() => handleShare(invoice)}
                          disabled={sharingId === invoice.id}
                          title={shareSuccess === invoice.id ? 'Link copied!' : 'Copy shareable link'}
                          className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                            shareSuccess === invoice.id
                              ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                              : 'text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                          }`}
                        >
                          {sharingId === invoice.id ? <RefreshCw className="w-4 h-4 animate-spin" /> :
                            shareSuccess === invoice.id ? <CheckCircle className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                        </button>
                      )}
                      {/* Email — locked on imported */}
                      {isImported(invoice) ? (
                        <span className="p-2 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Complete the invoice to email it">
                          <Mail className="w-4 h-4" />
                        </span>
                      ) : (
                        <div className="relative">
                          <button
                            onClick={() => handleOpenEmail(invoice)}
                            disabled={emailModal.loadingId === invoice.id}
                            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                            title={invoice.last_emailed_at ? `Last sent ${timeAgo(invoice.last_emailed_at, now)}` : 'Email invoice to client'}
                          >
                            {emailModal.loadingId === invoice.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          </button>
                          {invoice.last_emailed_at && (
                            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 ring-1 ring-white dark:ring-gray-900 pointer-events-none" />
                          )}
                        </div>
                      )}
                      {/* WhatsApp — locked on imported */}
                      {isImported(invoice) ? (
                        <span className="p-2 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Complete the invoice to share via WhatsApp">
                          <MessageCircle className="w-4 h-4" />
                        </span>
                      ) : (
                        <button
                          onClick={() => handleWhatsAppShare(invoice)}
                          disabled={sharingId === invoice.id}
                          title="Share via WhatsApp"
                          className="p-2 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {sharingId === invoice.id
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <MessageCircle className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                    {/* Download / Complete */}
                    {isImported(invoice) ? (
                      <button
                        onClick={() => handleEdit(invoice)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-amber-500 hover:bg-amber-600 text-white"
                        title="Fill in details to complete this invoice"
                      >
                        <Pencil className="w-4 h-4" />
                        <span>Complete</span>
                      </button>
                    ) : canDownloadPdf ? (
                      <button
                        onClick={() => handleDownload(invoice.id, invoice.invoice_number)}
                        disabled={downloadingId === invoice.id}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          downloadSuccess === invoice.id
                            ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                            : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                        title="Download PDF"
                      >
                        {downloadingId === invoice.id ? <RefreshCw className="w-4 h-4 animate-spin" /> :
                          downloadSuccess === invoice.id ? <CheckCircle className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                        <span>PDF</span>
                      </button>
                    ) : (
                      <span className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg cursor-not-allowed">
                        <Lock className="w-4 h-4" /> PDF
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Desktop table header (≥ sm) ── */}
            <div className="hidden sm:block px-6 py-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-600 dark:text-gray-400 items-center">
                {canDownloadPdf && (
                  <div className="col-span-1 flex items-center">
                    <button onClick={handleSelectAll} className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" title="Select all on page">
                      {allPageSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </button>
                  </div>
                )}
                <div className={canDownloadPdf ? "col-span-2" : "col-span-2"}>Invoice</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Date</div>
                <div className={canDownloadPdf ? "col-span-1 text-right" : "col-span-2 text-right"}>Total</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-2 text-center">Actions</div>
                <div className="col-span-1 text-right">PDF</div>
              </div>
            </div>

            {/* ── Desktop table body (≥ sm) ── */}
            <div className="hidden sm:block divide-y divide-gray-100 dark:divide-gray-800">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Checkbox — hidden for imported invoices (no PDF) */}
                    {canDownloadPdf && (
                      <div className="col-span-1 flex items-center">
                        {isImported(invoice) ? (
                          <span className="w-4 h-4" />
                        ) : (
                          <button onClick={() => handleToggleSelect(invoice.id)} className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                            {selectedIds.has(invoice.id) ? <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" /> : <Square className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    )}
                    {/* Invoice Number */}
                    <div className="col-span-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Link
                          to={`/history/${invoice.id}`}
                          state={fromState}
                          className="flex items-center hover:text-blue-600 transition-colors min-w-0"
                        >
                          <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2 flex-shrink-0" />
                          <span className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 underline-offset-2 hover:underline truncate">
                            {invoice.invoice_number}
                          </span>
                        </Link>
                        {wasImported(invoice) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 flex-shrink-0">
                            Imported
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Type */}
                    <div className="col-span-2">
                      <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                        {invoiceTypeNames[invoice.invoice_type] || invoice.invoice_type}
                      </span>
                    </div>
                    {/* Date */}
                    <div className="col-span-2 text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(invoice.created_at)}
                    </div>
                    {/* Total */}
                    <div className={`${canDownloadPdf ? "col-span-1" : "col-span-2"} text-right`}>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(invoice.total, invoice.currency)}
                      </span>
                    </div>
                    {/* Status */}
                    <div className="col-span-1 flex justify-center">
                      <button
                        onClick={() => handleToggleStatus(invoice)}
                        disabled={statusUpdating === invoice.id}
                        title={invoice.payment_status === 'paid' ? 'Mark as unpaid' : 'Mark as paid'}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold transition-opacity disabled:opacity-50 cursor-pointer ${STATUS_STYLES[invoice.payment_status] || STATUS_STYLES.unpaid}`}
                      >
                        {statusUpdating === invoice.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          invoice.payment_status === 'paid' ? 'Paid' :
                          invoice.payment_status === 'overdue' ? 'Overdue' : 'Unpaid'
                        )}
                      </button>
                    </div>
                    {/* Actions: Edit + Share + Email — locked on imported */}
                    <div className="col-span-2 flex items-center justify-center gap-1">
                      {/* Edit */}
                      <button
                        onClick={() => handleEdit(invoice)}
                        className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title={isImported(invoice) ? 'Complete invoice' : 'Edit invoice'}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {/* Share — locked on imported */}
                      {isImported(invoice) ? (
                        <span className="p-1.5 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Complete the invoice to share it">
                          <Link2 className="w-4 h-4" />
                        </span>
                      ) : (
                        <button
                          onClick={() => handleShare(invoice)}
                          disabled={sharingId === invoice.id}
                          title={shareSuccess === invoice.id ? 'Link copied!' : 'Copy shareable link'}
                          className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                            shareSuccess === invoice.id
                              ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                              : 'text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                          }`}
                        >
                          {sharingId === invoice.id
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : shareSuccess === invoice.id
                              ? <CheckCircle className="w-4 h-4" />
                              : <Link2 className="w-4 h-4" />}
                        </button>
                      )}
                      {/* Email — locked on imported */}
                      {isImported(invoice) ? (
                        <span className="p-1.5 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Complete the invoice to email it">
                          <Mail className="w-4 h-4" />
                        </span>
                      ) : (
                        <div className="relative">
                          <button
                            onClick={() => handleOpenEmail(invoice)}
                            disabled={emailModal.loadingId === invoice.id}
                            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                            title={invoice.last_emailed_at ? `Last sent ${timeAgo(invoice.last_emailed_at, now)}` : 'Email invoice to client'}
                          >
                            {emailModal.loadingId === invoice.id
                              ? <RefreshCw className="w-4 h-4 animate-spin" />
                              : <Mail className="w-4 h-4" />}
                          </button>
                          {invoice.last_emailed_at && (
                            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 ring-1 ring-white dark:ring-gray-900 pointer-events-none" />
                          )}
                        </div>
                      )}
                      {/* WhatsApp — locked on imported */}
                      {isImported(invoice) ? (
                        <span className="p-1.5 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Complete the invoice to share via WhatsApp">
                          <MessageCircle className="w-4 h-4" />
                        </span>
                      ) : (
                        <button
                          onClick={() => handleWhatsAppShare(invoice)}
                          disabled={sharingId === invoice.id}
                          title="Share via WhatsApp"
                          className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {sharingId === invoice.id
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <MessageCircle className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                    {/* Download / Complete */}
                    <div className="col-span-1 flex justify-end">
                      {isImported(invoice) ? (
                        <button
                          onClick={() => handleEdit(invoice)}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                          title="Fill in details to complete this invoice"
                        >
                          <Pencil className="w-3 h-3" />
                          Complete
                        </button>
                      ) : canDownloadPdf ? (
                        <button
                          onClick={() => handleDownload(invoice.id, invoice.invoice_number)}
                          disabled={downloadingId === invoice.id}
                          className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                            downloadSuccess === invoice.id
                              ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                              : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                          }`}
                          title="Download PDF"
                        >
                          {downloadingId === invoice.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : downloadSuccess === invoice.id ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                      ) : (
                        <span
                          className="p-1.5 text-gray-300 dark:text-gray-600 cursor-not-allowed"
                          title="PDF download is not available for your plan"
                        >
                          <Lock className="w-4 h-4" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Pagination footer ── */}
            <div className="px-4 sm:px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              {/* Mobile pagination */}
              <div className="flex sm:hidden items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">{paginationInfo}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {currentPage}/{totalPages}
                  </span>
                  <button
                    onClick={handlePrevPage}
                    disabled={!hasPrev || loading}
                    className="p-1.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={!hasNext || loading}
                    className="p-1.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Desktop pagination */}
              <div className="hidden sm:flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{paginationInfo}</p>
                  <div className="flex items-center space-x-2">
                    <label htmlFor="pageSize" className="text-sm text-gray-600 dark:text-gray-400">Per page:</label>
                    <select
                      id="pageSize"
                      value={limit}
                      onChange={handlePageSizeChange}
                      className="text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={handlePrevPage}
                    disabled={!hasPrev || loading}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Prev
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={!hasNext || loading}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>}

      {/* Email Invoice Modal */}
      {emailModal.open && emailModal.formData && (
        <EmailInvoiceModal
          formData={emailModal.formData}
          invoiceType={emailModal.invoiceType}
          lastEmailedAt={emailModal.lastEmailedAt}
          emailCount={emailModal.emailCount}
          onClose={() => setEmailModal({ open: false, formData: null, invoiceType: null, loadingId: null, lastEmailedAt: null, emailCount: 0 })}
        />
      )}

      {/* Import CSV Modal */}
      {importModal && <ImportCsvModal onClose={() => setImportModal(false)} onImported={() => { setImportModal(false); fetchInvoices(); }} />}

      {/* Upgrade modal (feature-gate prompts inside Pro content) */}
      {upgradeModal.open && (
        <UpgradeModal reason={upgradeModal.reason} onClose={() => setUpgradeModal({ open: false, reason: '' })} />
      )}
    </div>
  );
}

// ── Import CSV Modal ────────────────────────────────────────────────────────
function ImportCsvModal({ onClose, onImported }) {
  const [file, setFile]       = useState(null);
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const fileRef               = useRef(null);

  async function handleTemplateDownload() {
    try {
      const { blob, filename } = await downloadImportTemplate();
      downloadCSV(blob, filename);
    } catch (err) {
      setError('Failed to download template.');
    }
  }

  async function handleImport() {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const res = await importInvoicesCsv(file);
      setResult(res);
    } catch (err) {
      setError(err.message || 'Import failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" /> Import Invoice History
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><X className="w-5 h-5" /></button>
        </div>

        {!result ? (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Upload a CSV file to import your existing invoice history. Imported records appear with an <span className="font-semibold text-amber-600 dark:text-amber-400">Imported</span> badge — complete them to generate a PDF and unlock all actions.
            </p>
            <button onClick={handleTemplateDownload} className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Download CSV template
            </button>

            <div
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                file
                  ? 'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-950/20'
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
              }`}
            >
              {file ? (
                <>
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-green-700 dark:text-green-400">{file.name}</p>
                  <p className="text-xs text-green-600 dark:text-green-500 mt-1">{(file.size / 1024).toFixed(1)} KB — click to change</p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Click to select a CSV file</p>
                </>
              )}
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            <div className="flex gap-3 justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">Cancel</button>
              <button
                onClick={handleImport}
                disabled={!file || loading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                {loading ? 'Importing…' : 'Import'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">{result.imported} invoice{result.imported !== 1 ? 's' : ''} imported</p>
                  {result.skipped > 0 && <p className="text-xs text-green-700 dark:text-green-400">{result.skipped} skipped (already exist)</p>}
                </div>
              </div>
              {result.errors?.length > 0 && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-1.5">{result.errors.length} row{result.errors.length !== 1 ? 's' : ''} had errors:</p>
                  <ul className="space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <li key={i} className="text-xs text-yellow-700 dark:text-yellow-400">Row {e.row}: {e.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <button onClick={onImported} className="w-full px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
